import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels"; // Import the plugin

// Register Chart.js components and the datalabels plugin
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

export default function BarChart({
  data,
  xKey = "Threat", // Default key for x-axis labels
  stacked = false, // Toggle stacked vs simple bar chart
  xLabel = "", // Custom x-axis label
  yLabel = "", // Custom y-axis label
  title = "", // New optional title prop
  horizontal = false, // New prop to toggle horizontal/vertical bars, defaults to false
  fixedColors = [
    "#3D8D7A", // 3D8D7A
    "#B3D8A8", // B3D8A8
    "#A3D1C6", // A3D1C6
    "#4C7B8B", // 4C7B8B
  ],
}) {
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Dynamically determine the structure based on the first data item
    const firstItem = data[0];
    const dataStructure = Object.keys(firstItem).reduce((acc, key) => {
      acc[key] = typeof firstItem[key] === "number" ? 0 : "";
      return acc;
    }, {});

    // Validate data structure
    const validatedData = data.map((item) => ({
      ...dataStructure,
      ...item,
    }));

    // Extract x-axis labels
    const labels = validatedData.map((item) => item[xKey]);

    // Get all keys except the xKey to create datasets
    const valueKeys = Object.keys(firstItem).filter((key) => key !== xKey);

    // Prepare datasets dynamically
    const datasets = valueKeys.map((key, index) => ({
      label: key, // Use the key as the label (e.g., "TEF min", "TEF max", "TEF avg")
      data: validatedData.map((item) => item[key]),
      backgroundColor: fixedColors[index % fixedColors.length], // Use fixed colors
      borderWidth: 1,
      borderColor: "#fff",
      hidden: false, // Initially, all datasets are visible
    }));

    // Prepare the chart data
    const formattedChartData = {
      labels,
      datasets,
    };

    setChartData(formattedChartData);
    setIsLoading(false);
  }, [data, xKey]);

  // Chart options for dynamic bar chart
  const options = {
    indexAxis: horizontal ? "y" : "x", // Switch to "y" for horizontal bars, "x" for vertical
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked, // Toggle stacking based on prop
        title: {
          display: true,
          text: xLabel,
        },
      },
      y: {
        stacked, // Toggle stacking based on prop
        beginAtZero: true,
        title: {
          display: true,
          text: yLabel,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 8,
          font: {
            size: 12,
            family: "Arial",
          },
          color: "#333",
          usePointStyle: false, // Use bars for legend
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.datasetIndex;

          // Check if the clicked dataset is already the only visible one
          const isOnlyDatasetVisible =
            chart.data.datasets.filter((dataset) => !dataset.hidden).length ===
              1 && !chart.data.datasets[index].hidden;

          // If the clicked dataset is already the only visible one, show all datasets
          if (isOnlyDatasetVisible) {
            chart.data.datasets.forEach((dataset) => {
              dataset.hidden = false; // Show all datasets
            });
          } else {
            // Otherwise, hide all datasets except the clicked one
            chart.data.datasets.forEach((dataset, i) => {
              dataset.hidden = i !== index;
            });
          }

          chart.update(); // Update the chart to reflect changes
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label;
            const value = context.raw;
            const xValue = context.chart.data.labels[context.dataIndex];
            return `${xValue} - ${datasetLabel}: ${value}`;
          },
        },
      },
      datalabels: {
        display: true, // Enable data labels
        color: "#fff", // Set the label color to white
        font: {
          weight: "bold", // Bold font for better visibility
          size: 12, // Font size
        },
        formatter: (value) => {
          return value; // Display only the numeric value
        },
      },
    },
  };

  const chartHeight = 400; // Fixed height, adjust as needed

  return (
    <div style={{ height: `${chartHeight}px`, width: "100%" }}>
      {title && (
        <h3
          style={{
            textAlign: "center",
            marginBottom: "10px",
            fontFamily: "Arial, sans-serif",
            color: "#333",
          }}
        >
          {title}
        </h3>
      )}
      {isLoading ? (
        <p>Loading chart...</p>
      ) : chartData ? (
        <Bar data={chartData} options={options} />
      ) : (
        <p>No chart data available.</p>
      )}
    </div>
  );
}
